import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./SupportTicketsList.module.css";
import FeatureHeader from "../components/FeatureHeader";
import DataFallback from "../components/feedback/DataFallback";
import { classifyError } from "../utils/errorTaxonomy";
import { resilientFetch, TIMEOUT_NORMAL } from "../utils/resilientFetch";

const CALENDLY_CONFIRM_MSG =
  "Please book a call only if your issue requires a detailed discussion. For most queries, our team will resolve it over chat.";

const SupportTicketsList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ticketsPerPage = 12;

  const [bookCallTicket, setBookCallTicket] = useState(null);
  const [reopenTicket, setReopenTicket] = useState(null);
  const [reopenContext, setReopenContext] = useState("");
  const [reopenLoading, setReopenLoading] = useState(false);
  const [escalatingId, setEscalatingId] = useState(null);
  const [escalatedIds, setEscalatedIds] = useState(new Set());

  const navigate = useNavigate();
  const safeTickets = Array.isArray(tickets) ? tickets : [];

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await resilientFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
        { timeoutMs: TIMEOUT_NORMAL, fetchImpl: (u, o) => fetch(u, o) }
      );
      if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        throw err;
      }
      const data = await res.json();
      setTickets(Array.isArray(data) ? data : data.tickets || data.results || []);
      setError(null);
    } catch (err) {
      // Keeps any tickets already on screen; the classified description says
      // what happened without naming a status or assigning blame.
      setError(err && err.classified ? err.classified : classifyError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const filteredTickets = safeTickets.filter((t) => {
    const matchStatus = statusFilter === "all" || t.status === statusFilter;
    const matchPriority = priorityFilter === "all" || t.urgency === priorityFilter;
    const matchSearch =
      t.display_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchPriority && matchSearch;
  });

  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);
  const currentTickets = filteredTickets.slice(
    (currentPage - 1) * ticketsPerPage,
    currentPage * ticketsPerPage
  );

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
      timeZone: "Asia/Kolkata",
    });

  const formatDateTime = (d) =>
    new Date(d).toLocaleString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZone: "Asia/Kolkata",
    }) + " IST";

  const handleEscalate = async (ticket, e) => {
    e.stopPropagation();
    if (!ticket.can_escalate || escalatingId) return;
    setEscalatingId(ticket.id);
    setEscalatedIds(prev => new Set(prev).add(ticket.id)); // hide immediately
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/${ticket.id}/escalate/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) {
        const err = await res.json();
        setEscalatedIds(prev => { const s = new Set(prev); s.delete(ticket.id); return s; }); // restore on failure
        alert(err?.error?.message || "Escalation failed.");
      } else {
        await fetchTickets();
      }
    } catch {
      setEscalatedIds(prev => { const s = new Set(prev); s.delete(ticket.id); return s; }); // restore on failure
      alert("Escalation request failed.");
    } finally {
      setEscalatingId(null);
    }
  };

  const handleReopenSubmit = async () => {
    if (!reopenTicket || !reopenContext.trim()) return;
    setReopenLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/helpdesk/tickets/${reopenTicket.id}/reopen/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ context: reopenContext }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        alert(err?.error?.message || "Reopen failed.");
      } else {
        const ticketId = reopenTicket.id;
        setReopenTicket(null);
        setReopenContext("");
        // Navigate to chat thread so user can continue the conversation
        navigate(`/suspire-helpdesk/tickets/${ticketId}`);
      }
    } catch {
      alert("Reopen request failed.");
    } finally {
      setReopenLoading(false);
    }
  };

  const statusLabel = (s) => s.replace("_", " ");

  const statusClass = (s) => {
    switch (s) {
      case "open": return styles.statusOpen;
      case "in_progress": return styles.statusInProgress;
      case "resolved": return styles.statusResolved;
      case "closed": return styles.statusClosed;
      default: return "";
    }
  };

  const urgencyClass = (u) => {
    switch (u) {
      case "low": return styles.urgencyLow;
      case "medium": return styles.urgencyMedium;
      case "high": return styles.urgencyHigh;
      case "critical": return styles.urgencyCritical;
      default: return "";
    }
  };

  // No early return for loading or error. The header, "+ New Ticket", search and
  // the filters are all still usable when the list is missing — and raising a
  // ticket is a sensible thing to do when your tickets will not load, so taking
  // that button away was exactly backwards.

  return (
    <div className={styles.page}>
      {/* Book-a-Call popup */}
      {bookCallTicket && (
        <div className={styles.modalOverlay} onClick={() => setBookCallTicket(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Need deeper assistance?</h3>
            <p className={styles.modalBody}>{CALENDLY_CONFIRM_MSG}</p>
            <div className={styles.modalActions}>
              <button
                className={styles.modalPrimaryBtn}
                onClick={() => {
                  window.open(bookCallTicket.category_calendly_url, "_blank", "noopener,noreferrer");
                  setBookCallTicket(null);
                }}
              >
                Confirm & Open Calendar
              </button>
              <button className={styles.modalSecondaryBtn} onClick={() => setBookCallTicket(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen popup */}
      {reopenTicket && (
        <div className={styles.modalOverlay} onClick={() => { setReopenTicket(null); setReopenContext(""); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Reopen Ticket</h3>
            <p className={styles.modalBody}>
              Please share the reason for reopening. It will be added to the conversation thread.
            </p>
            <textarea
              className={styles.modalTextarea}
              placeholder="Describe why you need to reopen this ticket…"
              value={reopenContext}
              onChange={(e) => setReopenContext(e.target.value)}
              rows={4}
              maxLength={500}
            />
            <div className={styles.modalActions}>
              <button
                className={styles.modalPrimaryBtn}
                onClick={handleReopenSubmit}
                disabled={!reopenContext.trim() || reopenLoading}
              >
                {reopenLoading ? "Submitting…" : "Reopen Ticket"}
              </button>
              <button
                className={styles.modalSecondaryBtn}
                onClick={() => { setReopenTicket(null); setReopenContext(""); }}
                disabled={reopenLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className={styles.pageHeader}>
        <div>
          <FeatureHeader
            src="/images/feature-headers/suspire-helpdesk.png"
            alt="Suspire Helpdesk"
            style={{ marginBottom: "6px" }}
          />
          <p className={styles.pageSubtitle}>Track and manage your support requests</p>
        </div>
        <button
          className={styles.newTicketBtn}
          onClick={() => navigate("/suspire-helpdesk/create")}
        >
          + New Ticket
        </button>
      </div>

      {/* Filters bar */}
      <div className={styles.filtersBar}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search by ID or title…"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
        />
        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select
          className={styles.filterSelect}
          value={priorityFilter}
          onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
        <span className={styles.resultCount}>
          {filteredTickets.length} ticket{filteredTickets.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Ticket table */}
      {loading && currentTickets.length === 0 ? (
        <div className={styles.loadingRow}>Loading tickets…</div>
      ) : error && currentTickets.length === 0 ? (
        /*
          Ahead of the empty check: "No tickets found" is only true once a
          request has come back. Saying it after a failed one tells the user
          they have no tickets when we merely could not read them — and invites
          them to raise a duplicate.
        */
        <DataFallback variant="table" error={error} onRetry={fetchTickets} />
      ) : currentTickets.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>🎫</span>
          <h3>No tickets found</h3>
          <p>Create a new ticket to get help from our team.</p>
        </div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            {/* Table header */}
            <div className={styles.tableHead}>
              <span className={styles.colId}>Ticket ID</span>
              <span className={styles.colTitle}>Subject</span>
              <span className={styles.colCategory}>Category</span>
              <span className={styles.colDate}>Raised On</span>
              <span className={styles.colStatus}>Status</span>
              <span className={styles.colPriority}>Priority</span>
              <span className={styles.colMsgs}>Last Activity</span>
              <span className={styles.colActions}>Actions</span>
            </div>

            {/* Rows */}
            {currentTickets.map((ticket) => {
              const isResolvable = ticket.status === "resolved" || ticket.status === "closed";
              const alreadyEscalated = ticket.escalated || escalatedIds.has(ticket.id);

              return (
                <div
                  key={ticket.id}
                  className={`${styles.tableRow} ${ticket.is_pinned ? styles.pinnedRow : ""}`}
                  onClick={() => navigate(`/suspire-helpdesk/tickets/${ticket.id}`)}
                >
              <span className={styles.colId}>
                    <span className={styles.ticketId}>{ticket.display_id}</span>
                  </span>

                  <span className={styles.colTitle}>
                    <span className={styles.ticketTitle}>{ticket.title}</span>
                    {ticket.is_pinned && (
                      <span
                        className={styles.pinnedTag}
                        title="Your permanent main thread with Suspire"
                      >
                        📌 Pinned
                      </span>
                    )}
                  </span>

                  <span className={styles.colCategory}>
                    <span className={styles.categoryTag}>{ticket.category_name || "—"}</span>
                  </span>

                  <span className={styles.colDate}>{formatDate(ticket.created_at)}</span>

                  <span className={styles.colStatus}>
                    <span className={`${styles.badge} ${statusClass(ticket.status)}`}>
                      {statusLabel(ticket.status)}
                    </span>
                  </span>

                  <span className={styles.colPriority}>
                    <span className={`${styles.badge} ${urgencyClass(ticket.urgency)}`}>
                      {ticket.urgency}
                    </span>
                  </span>

                  <span className={styles.colMsgs}>
                    <span className={styles.msgCount}>{ticket.last_activity_at ? formatDateTime(ticket.last_activity_at) : '—'}</span>
                  </span>

                  {/* Actions — stop row click propagation */}
                  <span className={styles.colActions} onClick={(e) => e.stopPropagation()}>
                    <span className={styles.escalateGroup}>
                    {/* Escalate — hidden for the pinned main thread, which never escalates */}
                    {ticket.is_pinned ? null : alreadyEscalated ? (
                      <span
                        className={styles.tooltipWrapper}
                        data-tooltip={ticket.escalated_at ? `Escalated on ${formatDateTime(ticket.escalated_at)}` : "Already escalated"}
                      >
                        <span className={styles.escalatedBadge}>Escalated ✓</span>
                      </span>
                    ) : (
                      <span
                        className={styles.tooltipWrapper}
                        data-tooltip={!ticket.can_escalate ? "Escalation is available after 48 hours of raising tickets" : undefined}
                      >
                        <button
                          className={styles.actionBtn}
                          data-variant="escalate"
                          disabled={!ticket.can_escalate || escalatingId === ticket.id}
                          onClick={(e) => handleEscalate(ticket, e)}
                        >
                          {escalatingId === ticket.id ? "…" : "Escalate"}
                        </button>
                      </span>
                    )}

                    {/* Need deeper assistance? Book a call — for all tickets with a Calendly URL */}
                    {ticket.category_calendly_url && (
                      <button
                        className={styles.actionBtn}
                        data-variant="call"
                        onClick={(e) => { e.stopPropagation(); setBookCallTicket(ticket); }}
                      >
                        Need deeper assistance?<br />📅 Book a call
                      </button>
                    )}
                    </span>

                    {/* Reopen */}
                    {isResolvable && (
                      <button
                        className={styles.actionBtn}
                        data-variant="reopen"
                        onClick={(e) => { e.stopPropagation(); setReopenTicket(ticket); setReopenContext(""); }}
                      >
                        Reopen
                      </button>
                    )}

                    {/* Open chat arrow */}
                    <button
                      className={styles.openBtn}
                      onClick={(e) => { e.stopPropagation(); navigate(`/suspire-helpdesk/tickets/${ticket.id}`); }}
                      title="Open conversation"
                    >
                      →
                    </button>
                  </span>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                ← Prev
              </button>
              <span className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SupportTicketsList;
